import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ErrorMessage from '../../../Elements/ErrorMessage';
import FigmaButton from '../../../Elements/FigmaButton';
import InputWithClear from '../../../Elements/InputComponents/InputWithClear';
import OverWindow from '../../../Elements/TabList/OverWindow';
import ButtonColorsEnum from '../../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../../Enums/IconPositionEnum';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
// Lower-case file name on disk; a capitalised import breaks a case-sensitive build
import Loading from '../../../Elements/loading';
import fileHelper from '../../../Helpers/fileHelper';
import TemplateService from '../../../Services/TemplateService';
import { buildPreviewDocument, buildTabEdit, findSectionIssues, TextEdit } from './templateContentTools';

// Reads and edits a text template in place, beside a live preview of it. Only for a variant stored as
// text (IsStringContent), which today means HTML - a spreadsheet or a document is still downloaded,
// edited in its own program and uploaded back.
//
// There is no editing endpoint and none is needed: the file is read through the ordinary download
// (already converted to UTF-8 on the way out) and saved through the ordinary upload, as a file named
// after the template. So a save is a new version like any other, the stored bytes come out exactly as
// a download-edit-re-upload round trip would leave them, and the backend still resolves the newest
// version of the language at the moment the upload arrives.
//
// The newest version of a language is editable; an older one opens read-only, because a version is
// only ever stored on top of the newest one.

// Whichever list opened it: a variant row carries all of this, and the grouped list seeds the same
// shape from its newest row (where Version is the group's NewestVersion).
type TemplateVariant = {
  TemplateId: number;
  Name: string;
  Locale: string;
  Type: string;
  Version?: number;
  IsLatestVersion: boolean;
};

type Props = {
  item: TemplateVariant;
  clickOnCancel: () => void;
  clickOnSave: () => void;
};

// The email templates are 600px-wide table layouts, so a full-width preview misrepresents them;
// the toggle exists because a personalized-webpage template is a full-width page instead.
const EmailPreviewWidth = 600;

const PaneStyle: React.CSSProperties = {
  flex: '1 1 50%',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
};

const EditorStyle: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace",
  fontSize: '12px',
  lineHeight: 1.45,
  width: '100%',
  height: '100%',
  minHeight: '60vh',
  resize: 'none',
};

const TemplateContentEditor = ({ item, clickOnCancel, clickOnSave }: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [storedText, setStoredText] = useState<string>('');
  const [text, setText] = useState<string>('');
  const [highlight, setHighlight] = useState<boolean>(true);
  const [fullWidthPreview, setFullWidthPreview] = useState<boolean>(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  // Where the caret goes after an edit this component made rather than the browser
  const pendingSelection = useRef<[number, number] | null>(null);

  const readOnly = item.IsLatestVersion !== true;
  const isChanged = text !== storedText;

  useEffect(() => {
    let abandoned = false;

    TemplateService.GetTemplateById(item.TemplateId)
      .then((response: any) => {
        if (abandoned) {
          return;
        }
        if (!response || !response.binaryContent) {
          ErrorMessage.SetErrorMessage('The template has no stored content to show.');
          setLoading(false);
          return;
        }
        const content = fileHelper.base64ToUtf8Text(response.binaryContent);
        setStoredText(content);
        setText(content);
        setLoading(false);
      })
      .catch((error: any) => {
        if (abandoned) {
          return;
        }
        setLoading(false);
        ErrorMessage.SetErrorMessage(
          error?.isBusinessError
            ? error.message
            : 'Error! ErrorCode: TCE88. The template content could not be read. Please notify IT!'
        );
      });

    return () => {
      abandoned = true;
    };
  }, [item.TemplateId]);

  // Recomputed on every keystroke on purpose: both are cheap string passes, and the point of showing
  // them live is that a broken section tag is noticed while it is being typed.
  const sectionIssues = useMemo(() => findSectionIssues(text), [text]);
  const previewDocument = useMemo(() => buildPreviewDocument(text, highlight), [text, highlight]);

  // Only ever set by an edit this component made: the browser looks after the caret for typing.
  useEffect(() => {
    const selection = pendingSelection.current;
    if (selection === null || editorRef.current === null) {
      return;
    }
    pendingSelection.current = null;
    editorRef.current.setSelectionRange(selection[0], selection[1]);
  }, [text]);

  /**
   * Applies an edit the component decided on. `insertText` is tried first because it goes through the
   * browser, which keeps Ctrl+Z working and fires the change event itself; where it is unavailable
   * (or refuses) the state is set directly and the caret restored after the render.
   */
  const applyEdit = (field: HTMLTextAreaElement, edit: TextEdit) => {
    field.setSelectionRange(edit.from, edit.to);

    let insertedByBrowser = false;
    try {
      insertedByBrowser =
        typeof document.execCommand === 'function' && document.execCommand('insertText', false, edit.replacement);
    } catch (e) {
      insertedByBrowser = false;
    }

    if (insertedByBrowser) {
      // insertText leaves the caret at the end of what it inserted; a block stays selected instead
      field.setSelectionRange(edit.selectionStart, edit.selectionEnd);
      return;
    }

    pendingSelection.current = [edit.selectionStart, edit.selectionEnd];
    setText(field.value.slice(0, edit.from) + edit.replacement + field.value.slice(edit.to));
  };

  // Tab belongs to the text here, not to the focus order: a box holding indented markup is unusable
  // if Tab jumps out of it. Shift+Tab outdents, and a selection of several lines moves as a block.
  const keyDownInEditor = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Tab' || readOnly) {
      return;
    }

    event.preventDefault();

    const field = event.currentTarget;
    const edit = buildTabEdit(field.value, field.selectionStart, field.selectionEnd, event.shiftKey);

    if (edit !== null) {
      applyEdit(field, edit);
    }
  };

  const save = useCallback(() => {
    setSaving(true);

    TemplateService.SaveTemplateContent(item, text)
      .then((response: any) => {
        setSaving(false);
        if (response?.error || response?.errorMessage) {
          ErrorMessage.SetErrorMessage(response.error ?? response.errorMessage);
          return;
        }
        ErrorMessage.SetSuccessMessage('The new version has been stored.');
        clickOnSave();
      })
      .catch((error: any) => {
        setSaving(false);
        ErrorMessage.SetErrorMessage(
          error?.isBusinessError
            ? error.message
            : 'Error! ErrorCode: TCE110. The new version could not be stored. Please notify IT!'
        );
      });
  }, [clickOnSave, item, text]);

  // The warnings are advisory - a template may legitimately be saved mid-edit - but they are the one
  // mistake that fails silently later, so saving past them is a deliberate answer rather than a click.
  const clickOnSaveButton = () => {
    if (sectionIssues.length === 0) {
      save();
      return;
    }

    ErrorMessage.SetApprove(
      <div>
        <div>This template has unbalanced section tags:</div>
        <ul className='text-left'>
          {sectionIssues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
        <div>A section whose tags do not match is left out of the finished message entirely.</div>
      </div>,
      save,
      'Save anyway'
    );
  };

  const clickOnCloseButton = () => {
    if (!isChanged) {
      clickOnCancel();
      return;
    }

    ErrorMessage.SetApprove('Your changes have not been stored. Close the editor and lose them?', clickOnCancel);
  };

  // The two view switches are ordinary app inputs (InputWithClear + OnOff), so they carry the same
  // switch, label and info tooltip as every other field in Bravo rather than a bare checkbox.
  const renderToolbar = () => (
    <div className='row align-items-end py-2'>
      <div className='col-12 col-lg-6'>
        <div className='ds-label'>{item.Type} template</div>
        <div>
          <b>{item.Name}</b> · {item.Locale}
          {item.Version === undefined || item.Version === null ? null : ` · version ${item.Version}`}
        </div>
        {readOnly ? (
          <div className='mt-1'>
            <span className='badge badge-secondary'>
              Read-only — a new version can only be stored on top of the newest one
            </span>
          </div>
        ) : (
          <div className='mt-1 text-muted'>
            <small>Tab indents inside the editor, Shift+Tab outdents. Every save is stored as the next version.</small>
          </div>
        )}
      </div>
      <div className='col-6 col-lg-3'>
        <InputWithClear
          type={InputTypesEnum.OnOff}
          fieldName='HighlightPlaceholders'
          labelText='Mark filled-in fields'
          tooltipText={
            'Marks the parts Bravo replaces when it sends the message: {{Key}}, [[Key]], {Key}, [Key]' +
            ' and the repeated or optional blocks. It changes the preview only, never what is stored.'
          }
          value={highlight}
          onChange={(fieldName: string, newValue: boolean) => setHighlight(newValue === true)}
        />
      </div>
      <div className='col-6 col-lg-3'>
        <InputWithClear
          type={InputTypesEnum.OnOff}
          fieldName='FullWidthPreview'
          labelText='Full-width preview'
          tooltipText={`Off shows the preview at ${EmailPreviewWidth} pixels, the width an email is read at. Switch it on for a template that is a web page.`}
          value={fullWidthPreview}
          onChange={(fieldName: string, newValue: boolean) => setFullWidthPreview(newValue === true)}
        />
      </div>
    </div>
  );

  const renderIssues = () => {
    if (sectionIssues.length === 0) {
      return null;
    }

    return (
      <div
        className='alert alert-warning py-2 mb-2'
        style={{ maxHeight: '8rem', overflowY: 'auto' }}
      >
        <b>Check the section tags:</b>
        <ul className='mb-0'>
          {sectionIssues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      </div>
    );
  };

  const renderPreview = () => (
    <div
      style={{
        flex: '1 1 auto',
        overflow: 'auto',
        border: '1px solid #ced4da',
        background: '#ffffff',
        minHeight: '60vh',
      }}
    >
      {/* Sandboxed with no permissions at all: no scripts, no forms, no access to the page around it.
          A template is a whole document with its own <head> and <style>, so it cannot be rendered
          inline either way - its styles would repaint the page behind it. */}
      <iframe
        title='Template preview'
        srcDoc={previewDocument}
        sandbox=''
        referrerPolicy='no-referrer'
        style={{
          display: 'block',
          border: 'none',
          height: '100%',
          minHeight: '60vh',
          width: fullWidthPreview ? '100%' : `${EmailPreviewWidth}px`,
          margin: fullWidthPreview ? undefined : '0 auto',
        }}
      />
    </div>
  );

  const renderPanes = () => (
    <div
      className='d-flex'
      style={{ gap: '0.5rem', alignItems: 'stretch' }}
    >
      <div style={PaneStyle}>
        <label className='ds-label mb-1'>{readOnly ? 'Stored content' : 'Content'}</label>
        <textarea
          ref={editorRef}
          className='form-control form-control-sm'
          style={EditorStyle}
          value={text}
          readOnly={readOnly}
          spellCheck={false}
          wrap='off'
          onChange={(event) => setText(event.target.value)}
          onKeyDown={keyDownInEditor}
        />
      </div>
      <div style={PaneStyle}>
        <label className='ds-label mb-1'>Preview</label>
        {renderPreview()}
      </div>
    </div>
  );

  // Nothing to render in read-only mode - the window's own close button is the only way out of it
  const renderButtons = () =>
    readOnly ? null : (
      <div className='d-flex justify-content-center py-3'>
        <FigmaButton
          label='Store As New Version'
          iconName={`fa-solid fa-floppy-disk${saving ? ' fa-spin' : ''}`}
          disabled={saving || loading || !isChanged}
          tooltip={isChanged ? 'Store the edited content as the next version' : 'Nothing has been changed yet'}
          type={ButtonTypeEnum.ExtraLargeNormal}
          color={ButtonColorsEnum.Primary}
          iconPosition={IconPositionEnum.Left}
          onClick={clickOnSaveButton}
        />
      </div>
    );

  return (
    <OverWindow
      title={`Template Content - ${item.Name} (${item.Locale}, version ${item.Version})`}
      clickOnCancel={() => {
        if (!saving) {
          clickOnCloseButton();
        }
      }}
      closeOnClickOutside={false}
      iconName='fa-solid fa-code'
      // Nested in the template's detail popup; OverWindow.css escalates a nested window's
      // .disable-outside-clicks by itself, so there is nothing to set for the stacking.
      // The body scrolls (the default): the warning list above the panes is of unknown height.
      className='top-of-boxFigma'
    >
      <div className='container-fluid'>
        {loading ? (
          <Loading />
        ) : (
          <>
            {renderToolbar()}
            {renderIssues()}
            {renderPanes()}
            {renderButtons()}
          </>
        )}
      </div>
    </OverWindow>
  );
};

export default TemplateContentEditor;
